import { debugLogger } from '@/utils/debugLogger';
import { getSignedMediaUrl } from '@/utils/signedMediaUtils';
import { convertToCdnUrl, CDN_CONFIG } from '@/config/cdn';

interface CachedSignedUrl {
  filePath: string;
  bucket: string;
  signedUrl: string;
  cdnUrl: string; // CDN-rewritten URL for faster delivery
  expiresAt: number; // timestamp
  generatedAt: number;
}

interface CacheStats {
  size: number;
  oldestEntry: number | null;
  newestEntry: number | null;
  hitRate: number;
  totalRequests: number;
  cacheHits: number;
}

/**
 * SignedUrlManager Singleton
 * 
 * Manages intelligent caching of Supabase signed URLs with automatic expiration detection
 * and regeneration. Designed for private profile_photos bucket.
 * 
 * Features:
 * - Automatic expiration detection (1h safety buffer before actual expiry)
 * - Cache key format: `{bucket}:{filePath}`
 * - Stats tracking (hit rate, cache size)
 * - Graceful fallback on errors
 * 
 * Usage:
 * ```typescript
 * const signedUrl = await signedUrlManager.getSignedUrl('abc/def-0.jpg', 'profile_photos');
 * ```
 */
class SignedUrlManager {
  private static instance: SignedUrlManager;
  private cache = new Map<string, CachedSignedUrl>();
  private readonly CACHE_BUFFER = 60 * 60 * 1000; // 1h safety buffer
  private readonly DEFAULT_EXPIRY = 24 * 60 * 60; // 24h expiration
  private stats: CacheStats = {
    size: 0,
    oldestEntry: null,
    newestEntry: null,
    hitRate: 0,
    totalRequests: 0,
    cacheHits: 0
  };

  private constructor() {
    debugLogger.debug('SignedUrlManager', 'Singleton instance created');
  }

  /**
   * Get singleton instance
   */
  static getInstance(): SignedUrlManager {
    if (!SignedUrlManager.instance) {
      SignedUrlManager.instance = new SignedUrlManager();
    }
    return SignedUrlManager.instance;
  }

  /**
   * Get signed URL with intelligent caching
   * 
   * @param filePath - File path in bucket (e.g., 'abc123/def456-0.jpg')
   * @param bucket - Storage bucket name (e.g., 'profile_photos')
   * @returns Signed URL or null on error
   */
  async getSignedUrl(filePath: string, bucket: string): Promise<string | null> {
    this.stats.totalRequests++;
    const cacheKey = this.getCacheKey(filePath, bucket);
    const cached = this.cache.get(cacheKey);

    // Check if cached and still valid (with 1h buffer)
    if (cached && !this.isExpired(cached)) {
      debugLogger.debug('SignedUrlManager', 'Cache hit', {
        filePath,
        bucket,
        expiresIn: Math.floor((cached.expiresAt - Date.now()) / 1000 / 60) + 'min',
        cdnEnabled: CDN_CONFIG.enabled
      });
      this.stats.cacheHits++;
      this.updateHitRate();
      // Return CDN URL for faster delivery via nginx cache
      return cached.cdnUrl;
    }

    // Cache miss or expired - regenerate
    debugLogger.debug('SignedUrlManager', cached ? 'Cache expired, regenerating' : 'Cache miss, generating', {
      filePath,
      bucket
    });

    try {
      const signedUrl = await getSignedMediaUrl(filePath, this.DEFAULT_EXPIRY, bucket);
      
      if (!signedUrl) {
        debugLogger.error('SignedUrlManager', 'Failed to generate signed URL', {
          filePath,
          bucket
        });
        return null;
      }

      // Convert to CDN URL for nginx caching
      const cdnUrl = convertToCdnUrl(signedUrl);
      
      // Cache the new signed URL with CDN version
      const now = Date.now();
      const cachedEntry: CachedSignedUrl = {
        filePath,
        bucket,
        signedUrl,
        cdnUrl,
        expiresAt: now + (this.DEFAULT_EXPIRY * 1000) - this.CACHE_BUFFER, // 23h actual cache
        generatedAt: now
      };

      this.cache.set(cacheKey, cachedEntry);
      this.updateCacheStats();
      this.updateHitRate();

      debugLogger.debug('SignedUrlManager', 'Signed URL cached with CDN', {
        filePath,
        bucket,
        cacheSize: this.cache.size,
        expiresIn: '23h',
        cdnEnabled: CDN_CONFIG.enabled,
        cdnUrl: cdnUrl.substring(0, 80)
      });

      // Return CDN URL for faster delivery
      return cdnUrl;
    } catch (error) {
      debugLogger.error('SignedUrlManager', 'Error generating signed URL', {
        filePath,
        bucket,
        error
      });
      
      // Return expired cached CDN URL as fallback if available
      if (cached) {
        debugLogger.warn('SignedUrlManager', 'Using expired cache as fallback');
        return cached.cdnUrl;
      }
      
      return null;
    }
  }

  /**
   * Preload multiple signed URLs in parallel
   */
  async preloadUrls(filePaths: string[], bucket: string): Promise<void> {
    const promises = filePaths.map(filePath => 
      this.getSignedUrl(filePath, bucket).catch(error => {
        debugLogger.warn('SignedUrlManager', 'Preload failed for:', filePath, error);
        return null;
      })
    );

    await Promise.allSettled(promises);
    debugLogger.debug('SignedUrlManager', 'Preload completed', {
      total: filePaths.length,
      bucket
    });
  }

  /**
   * Check if cached entry is expired (with safety buffer)
   */
  private isExpired(cached: CachedSignedUrl): boolean {
    const now = Date.now();
    return now >= cached.expiresAt;
  }

  /**
   * Generate cache key
   */
  private getCacheKey(filePath: string, bucket: string): string {
    return `${bucket}:${filePath}`;
  }

  /**
   * Update cache statistics
   */
  private updateCacheStats(): void {
    this.stats.size = this.cache.size;
    
    if (this.cache.size === 0) {
      this.stats.oldestEntry = null;
      this.stats.newestEntry = null;
      return;
    }

    let oldest = Infinity;
    let newest = 0;

    for (const cached of this.cache.values()) {
      if (cached.generatedAt < oldest) oldest = cached.generatedAt;
      if (cached.generatedAt > newest) newest = cached.generatedAt;
    }

    this.stats.oldestEntry = oldest;
    this.stats.newestEntry = newest;
  }

  /**
   * Update hit rate calculation
   */
  private updateHitRate(): void {
    this.stats.hitRate = this.stats.totalRequests > 0
      ? (this.stats.cacheHits / this.stats.totalRequests) * 100
      : 0;
  }

  /**
   * Clear all expired entries
   */
  clearExpired(): void {
    const now = Date.now();
    let removedCount = 0;

    for (const [key, cached] of this.cache.entries()) {
      if (this.isExpired(cached)) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    this.updateCacheStats();
    debugLogger.debug('SignedUrlManager', `Cleared ${removedCount} expired entries`);
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    this.cache.clear();
    this.updateCacheStats();
    this.stats = {
      size: 0,
      oldestEntry: null,
      newestEntry: null,
      hitRate: 0,
      totalRequests: 0,
      cacheHits: 0
    };
    debugLogger.info('SignedUrlManager', 'All cache cleared');
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Get cache size in entries
   */
  getCacheSize(): number {
    return this.cache.size;
  }
}

export const signedUrlManager = SignedUrlManager.getInstance();

// Auto cleanup expired entries every 10 minutes
const IS_TEST =
  (typeof process !== 'undefined' && (process.env.VITEST === 'true' || process.env.NODE_ENV === 'test')) ||
  (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE === 'test') ||
  (typeof window !== 'undefined' && (window as any).__VITEST_BROWSER__ === true);

if (!IS_TEST && typeof window !== 'undefined') {
  setInterval(() => {
    signedUrlManager.clearExpired();
  }, 10 * 60 * 1000); // Every 10 minutes
}
